import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { getMachineSafetyDetail, resolveMachineSafetyFileUrl } from "./machineSafetyApi";
import MachineSafetyHistoryDialog from "./MachineSafetyHistoryDialog";

const DRAWER_WIDTH = 340;

const RISK = {
  critical: { bg: "#7f1d1d", fg: "#fff", soft: "#fee2e2", label: "매우 높음" },
  veryHigh: { bg: "#dc2626", fg: "#fff", soft: "#fee2e2", label: "높음" },
  high: { bg: "#f97316", fg: "#fff", soft: "#ffedd5", label: "높음" },
  elevated: { bg: "#f59e0b", fg: "#111827", soft: "#fef3c7", label: "주의" },
  caution: { bg: "#a3e635", fg: "#1f2937", soft: "#ecfccb", label: "보통" },
  safe: { bg: "#16a34a", fg: "#fff", soft: "#dcfce7", label: "낮음" },
  neutral: { bg: "#64748b", fg: "#fff", soft: "#f1f5f9", label: "미평가" },
};

const STATUS = {
  normal: { label: "정상", color: "#16a34a", bg: "rgba(22,163,74,.16)" },
  improving: { label: "개선 진행", color: "#f59e0b", bg: "rgba(245,158,11,.17)" },
  overdue: { label: "기한 초과", color: "#ef4444", bg: "rgba(239,68,68,.17)" },
  evaluation_required: { label: "평가 필요", color: "#38bdf8", bg: "rgba(56,189,248,.16)" },
};

const HAZARD_STATUS = {
  not_evaluated: "미평가",
  measure_required: "개선 필요",
  improving: "개선 중",
  improvement_completed: "개선 완료",
  evaluated: "평가 완료",
};

const dateLabel = (value) => (value ? String(value).slice(0, 10) : "-");

function Section({ icon, title, action, children, sx }) {
  return (
    <Box component="section" sx={{ px: 1.5, py: 2.15, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.55 }}>
        <Stack direction="row" spacing={0.9} alignItems="center">
          <Box sx={{ color: "#a5b4fc", display: "grid", placeItems: "center" }}>{icon}</Box>
          <Typography sx={{ color: "#f8fafc", fontSize: 13.5, fontWeight: 800, lineHeight: 1.35 }}>{title}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function Metric({ icon, label, value, accent = "#60a5fa" }) {
  return (
    <Box sx={{ minWidth: 0, minHeight: 92, px: 1, py: 1.35, borderRadius: 1.5, bgcolor: "rgba(255,255,255,.055)", border: "1px solid rgba(255,255,255,.08)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
      <Stack alignItems="center" spacing={0.45}>
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", color: accent, bgcolor: `${accent}18`, display: "grid", placeItems: "center", "& svg": { fontSize: 17 } }}>{icon}</Box>
        <Typography sx={{ minWidth: 0, color: "#94a3b8", fontSize: 11.25, fontWeight: 700, lineHeight: 1.35 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ mt: 0.65, color: "#fff", fontSize: 21, fontWeight: 900, lineHeight: 1.1, textAlign: "center" }}>{value}</Typography>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.65 }}>
      <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: "#e2e8f0", fontSize: 12, fontWeight: 700, textAlign: "right", overflowWrap: "anywhere" }}>{value || "-"}</Typography>
    </Stack>
  );
}

function HazardRow({ item, onHistory }) {
  const tone = RISK[item.risk_tone] || RISK.neutral;
  return (
    <Paper
      component="button"
      type="button"
      onClick={() => onHistory(item.register_cause_id)}
      elevation={0}
      sx={{
        width: "100%",
        p: 1.25,
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        bgcolor: "rgba(255,255,255,.045)",
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 1.6,
        cursor: "pointer",
        transition: "background-color .16s ease, border-color .16s ease",
        "&:hover": { bgcolor: "rgba(255,255,255,.085)", borderColor: "rgba(129,140,248,.55)" },
      }}
    >
      <Stack direction="row" spacing={1.05} alignItems="flex-start">
        <Box sx={{ width: 5, alignSelf: "stretch", minHeight: 40, borderRadius: 3, bgcolor: tone.bg, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: "#f8fafc", fontSize: 12.25, fontWeight: 750, lineHeight: 1.5, overflowWrap: "anywhere" }}>
            {item.danger_description}
          </Typography>
          <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>
            <Chip size="small" label={item.process_name || "공정 미지정"} sx={{ height: 22, color: "#cbd5e1", bgcolor: "rgba(148,163,184,.13)", fontSize: 10.5 }} />
            <Chip size="small" label={item.risk_score == null ? "미평가" : `${item.effective_risk_score}점`} sx={{ height: 22, bgcolor: tone.bg, color: tone.fg, fontWeight: 900, fontSize: 10.5 }} />
            <Chip size="small" label={HAZARD_STATUS[item.status] || "확인 필요"} variant="outlined" sx={{ height: 22, color: item.status === "improvement_completed" ? "#4ade80" : "#fbbf24", borderColor: "currentColor", fontSize: 10.5 }} />
          </Stack>
        </Box>
        <ChevronRightIcon sx={{ mt: 0.5, color: "#64748b", fontSize: 18 }} />
      </Stack>
    </Paper>
  );
}

export default function MachineSafetyDrawer({
  open = true,
  onClose,
  groupCode,
  projectCode,
  linkId,
  variant = "temporary",
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCauseId, setHistoryCauseId] = useState(null);
  const [machineExpanded, setMachineExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getMachineSafetyDetail({ groupCode, projectCode, linkId });
      setData(result);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "설비 안전정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [groupCode, linkId, projectCode]);

  useEffect(() => {
    if (open) load();
  }, [load, open]);

  const status = STATUS[data?.summary?.overall_status] || STATUS.evaluation_required;
  const risk = RISK[data?.summary?.highest_risk_tone] || RISK.neutral;
  const locations = useMemo(() => (data?.locations || []).map((item) => item.location_name).join(", "), [data]);
  const processes = useMemo(() => (data?.processes || []).map((item) => item.process_name).join(", "), [data]);

  const showHistory = (causeId = null) => {
    setHistoryCauseId(causeId);
    setHistoryOpen(true);
  };

  const drawerPaperSx = {
    width: `min(100vw, ${DRAWER_WIDTH}px)`,
    maxWidth: "100vw",
    boxSizing: "border-box",
    border: 0,
    color: "#fff",
    bgcolor: "#0b1220",
    backgroundImage: "linear-gradient(180deg, rgba(15,23,42,.98), rgba(4,10,20,.985))",
    boxShadow: "-18px 0 46px rgba(0,0,0,.34)",
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant={variant}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 1.75, pt: 2.15, pb: 1.95, borderBottom: "1px solid rgba(255,255,255,.09)", bgcolor: "rgba(2,6,23,.78)" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Stack direction="row" spacing={1.15} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 1.8, bgcolor: "rgba(99,102,241,.2)", border: "1px solid rgba(129,140,248,.22)", color: "#a5b4fc", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ color: "#fff", fontSize: 18.5, fontWeight: 900, lineHeight: 1.25 }}>
                    {loading ? "설비 안전정보" : data?.machine?.display_name || "설비 정보 없음"}
                  </Typography>
                  <Typography noWrap sx={{ mt: 0.35, color: "#94a3b8", fontSize: 12 }}>
                    {data?.machine?.machine_manage_code || "관리번호 미등록"} · 연동 ID {data?.machine?.dt_sync_id || linkId}
                  </Typography>
                </Box>
              </Stack>
              {variant !== "permanent" && <IconButton aria-label="닫기" onClick={onClose} sx={{ color: "#cbd5e1" }}><CloseIcon /></IconButton>}
            </Stack>
            {!loading && data && (
              <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap sx={{ mt: 1.65 }}>
                <Chip size="small" label={status.label} sx={{ height: 25, bgcolor: status.bg, color: status.color, border: `1px solid ${status.color}55`, fontWeight: 900 }} />
                <Chip icon={<PlaceOutlinedIcon />} size="small" label={locations || "장소 미배정"} sx={{ height: 25, maxWidth: "100%", color: "#cbd5e1", bgcolor: "rgba(255,255,255,.065)", border: "1px solid rgba(255,255,255,.06)", "& .MuiChip-icon": { color: "#94a3b8" } }} />
                <Chip icon={<AccountTreeOutlinedIcon />} size="small" label={processes || "공정 미연결"} sx={{ height: 25, maxWidth: "100%", color: "#cbd5e1", bgcolor: "rgba(255,255,255,.065)", border: "1px solid rgba(255,255,255,.06)", "& .MuiChip-icon": { color: "#94a3b8" } }} />
              </Stack>
            )}
          </Box>

          <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", overflowX: "hidden", pt: 0.7, pb: 2, scrollbarWidth: "thin", scrollbarColor: "#475569 transparent", "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#475569", borderRadius: 8 } }}>
            {loading && (
              <Stack spacing={1.25} sx={{ p: 1.5 }}>
                <Skeleton variant="rounded" height={92} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
                <Skeleton variant="rounded" height={150} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
                <Skeleton variant="rounded" height={220} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
              </Stack>
            )}
            {!loading && error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}
            {!loading && data && (
              <>
                <Section title="이번 달 안전 현황" icon={<FactCheckOutlinedIcon fontSize="small" />} action={<Typography sx={{ color: "#64748b", fontSize: 10.5 }}>{data.lookup.month_start?.slice(0, 7)}</Typography>}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                    <Metric icon={<WarningAmberRoundedIcon fontSize="small" />} label="유해·위험요인" value={`${data.summary.hazard_count}건`} accent="#fbbf24" />
                    <Metric icon={<VerifiedOutlinedIcon fontSize="small" />} label="평가 완료" value={`${data.summary.evaluated_count}건`} accent="#4ade80" />
                    <Metric icon={<BuildCircleOutlinedIcon fontSize="small" />} label="개선 진행" value={`${data.summary.improving_count}건`} accent="#fb923c" />
                    <Metric icon={<WarningAmberRoundedIcon fontSize="small" />} label="최고 위험성" value={data.summary.highest_risk_score ? `${data.summary.highest_risk_score}점` : "-"} accent={risk.bg} />
                  </Box>
                </Section>

                {data.priority_item && (
                  <Section title="우선 확인 항목" icon={<WarningAmberRoundedIcon fontSize="small" />}>
                    <Box sx={{ p: 1.35, borderRadius: 1.7, bgcolor: "rgba(255,255,255,.055)", border: `1px solid ${risk.bg}66`, borderLeft: `4px solid ${risk.bg}`, boxShadow: "0 8px 20px rgba(0,0,0,.1)" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.8}>
                        <Chip size="small" label={data.priority_item.process_name || "공정 미지정"} sx={{ height: 22, color: "#cbd5e1", bgcolor: "rgba(148,163,184,.14)", fontSize: 10.5, fontWeight: 700 }} />
                        <Chip size="small" label={data.priority_item.effective_risk_score ? `${data.priority_item.effective_risk_score}점` : "미평가"} sx={{ height: 23, flexShrink: 0, bgcolor: risk.bg, color: risk.fg, fontWeight: 900, fontSize: 10.75 }} />
                      </Stack>
                      <Typography sx={{ mt: 1, color: "#f8fafc", fontSize: 12.5, fontWeight: 800, lineHeight: 1.55, overflowWrap: "anywhere" }}>
                        {data.priority_item.danger_description}
                      </Typography>
                      {data.priority_item.expected_date && (
                        <Typography sx={{ mt: 0.75, color: "#94a3b8", fontSize: 11.25, fontWeight: 650 }}>
                          개선 예정일 {dateLabel(data.priority_item.expected_date)}
                        </Typography>
                      )}
                    </Box>
                  </Section>
                )}

                <Section
                  title="위험성평가"
                  icon={<HistoryIcon fontSize="small" />}
                  action={<Button size="small" onClick={() => showHistory()} endIcon={<ChevronRightIcon />} sx={{ color: "#a5b4fc", minWidth: 0, px: 0.6, fontSize: 11 }}>전체 이력</Button>}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.65 }}>
                    <Typography sx={{ color: "#94a3b8", fontSize: 11.5 }}>평가 진행률</Typography>
                    <Typography sx={{ color: "#e2e8f0", fontSize: 11.5, fontWeight: 800 }}>{data.summary.evaluated_count}/{data.summary.hazard_count}건 · {data.summary.evaluation_rate}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={data.summary.evaluation_rate} sx={{ height: 6, borderRadius: 9, bgcolor: "rgba(255,255,255,.08)", "& .MuiLinearProgress-bar": { borderRadius: 9, bgcolor: "#6366f1" } }} />
                  <Stack spacing={0.85} sx={{ mt: 1.25 }}>
                    {data.hazards.length ? data.hazards.map((item) => <HazardRow key={item.register_cause_id} item={item} onHistory={showHistory} />) : <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>연결된 유해·위험요인이 없습니다.</Typography>}
                  </Stack>
                </Section>

                <Divider sx={{ borderColor: "rgba(255,255,255,.07)" }} />
                <Section title="사용 물질안전보건자료" icon={<ScienceOutlinedIcon fontSize="small" />}>
                  <Stack spacing={0.75}>
                    {data.msds.length ? data.msds.map((item) => (
                      <Box key={item.id} sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.07)" }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ color: "#f1f5f9", fontSize: 12.5, fontWeight: 750 }}>{item.product_name}</Typography>
                            <Typography noWrap sx={{ mt: 0.2, color: "#94a3b8", fontSize: 10.8 }}>{item.substance_name || "물질명 미등록"} · 만료 {dateLabel(item.expire_at)}</Typography>
                          </Box>
                          {item.pdf_url && <Tooltip title="원본 PDF 열기"><IconButton component="a" href={resolveMachineSafetyFileUrl(item.pdf_url)} target="_blank" rel="noreferrer" size="small" sx={{ color: "#a5b4fc" }}><OpenInNewIcon fontSize="inherit" /></IconButton></Tooltip>}
                        </Stack>
                      </Box>
                    )) : <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>이 설비에 연결된 MSDS가 없습니다.</Typography>}
                  </Stack>
                </Section>

                <Divider sx={{ borderColor: "rgba(255,255,255,.07)" }} />
                <Section
                  title="설비 기본정보"
                  icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
                  action={<IconButton aria-label={machineExpanded ? "접기" : "펼치기"} size="small" onClick={() => setMachineExpanded((value) => !value)} sx={{ color: "#94a3b8", transform: machineExpanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><ExpandMoreIcon fontSize="small" /></IconButton>}
                >
                  <Collapse in={machineExpanded} unmountOnExit>
                    <Box sx={{ px: 1.2, py: 0.7, bgcolor: "rgba(255,255,255,.035)", borderRadius: 1.5 }}>
                      <InfoRow label="제작사" value={data.machine.machine_manufacturer} />
                      <InfoRow label="모델명" value={data.machine.machine_model_name} />
                      <InfoRow label="규격" value={data.machine.machine_standard} />
                      <InfoRow label="구입일" value={data.machine.machine_purchase_date} />
                      <InfoRow label="크기" value={data.machine.machine_size} />
                      <InfoRow label="전압" value={data.machine.machine_voltage} />
                    </Box>
                  </Collapse>
                </Section>
              </>
            )}
          </Box>

          <Box sx={{ p: 1.2, borderTop: "1px solid rgba(255,255,255,.08)", bgcolor: "rgba(2,6,23,.92)" }}>
            <Stack direction="row" spacing={0.75}>
              <Button fullWidth size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => showHistory()} disabled={!data} sx={{ minHeight: 38, color: "#c7d2fe", borderColor: "rgba(165,180,252,.45)", whiteSpace: "nowrap", fontSize: 11.5 }}>평가 이력</Button>
              <Button fullWidth size="small" variant="contained" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/safe/${groupCode}/${projectCode}/register/cause?machine_id=${data?.machine?.machine_id || ""}`)} disabled={!data} sx={{ minHeight: 38, bgcolor: "#6366f1", whiteSpace: "nowrap", fontSize: 11.5, "&:hover": { bgcolor: "#4f46e5" } }}>위험요인 관리</Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <MachineSafetyHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        groupCode={groupCode}
        projectCode={projectCode}
        linkId={linkId}
        machineName={data?.machine?.display_name}
        initialCauseId={historyCauseId}
      />
    </>
  );
}
